import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { ProgressBar } from 'hooks';
import React from 'react';

/**
 * Interface for employer core responsibility
 */
export interface EmployerCoreResponsibility {
  id: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  responsibility1: string;
  check1: boolean;
  description1: string;
  responsibility2: string;
  description2: string;
  check2: boolean;
  responsibility3: string;
  description3: string;
  check3: boolean;
  responsibility4: string;
  description4: string;
  check4: boolean;
  responsibility5: string;
  description5: string;
  check5: boolean;
  assessment: number;
}

export interface CompanyInfo {
  id: number;
  company_logo: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  name: string;
  industry: string;
  hiring_frequency: string;
  no_of_employees: string;
  owner: number;
}


/**
 * Interface for employer personality qualification
 */
export interface EmployerPersonalityQualification {
  id: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  quality1: string;
  description1: string;
  most_prefered1: string;
  next_prefered1: string;
  least_prefered1: string;
  quality2: string;
  description2: string;
  most_prefered2: string;
  next_prefered2: string;
  least_prefered2: string;
  quality3: string;
  description3: string;
  most_prefered3: string;
  next_prefered3: string;
  least_prefered3: string;
  quality4: string;
  description4: string;
  most_prefered4: string;
  next_prefered4: string;
  least_prefered4: string;
  assessment: number;
}

/**
 * Interface for assessment data response
 */
export interface AssessmentData {
  id: number;
  assessment_id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  progress_bar: ProgressBar;
  start_date: string;
  end_date: string;
  job_description: string;
  instruction: string;
  time_zone: Record<string, any>;
  about?: string;
  employer_core_responsibility: EmployerCoreResponsibility;
  employer_personality_qualification: EmployerPersonalityQualification;
  company_info: CompanyInfo;
  uploaded_jd: File | null;
  duration_in_minutes: number;
  total_questions: number;
  basic_expectation: {
    id: number;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
    employment_type: 'fulltime' | 'parttime' | string;
    work_mode?: 'onsite' | 'remote' | 'hybrid' | 'on_site' | string;
    experience_level: 'entry' | 'mid' | 'senior' | string;
    experience_environment: 'remote' | 'on_site' | 'hybrid' | 'not_important' | string;
    years_of_experience: '0_1' | '2_5' | '5_plus' | string;
    address: string | null;
    country: string | null;
    region: string | null;
    salary_range_term: 'monthly' | 'yearly' | string;
    salary_range_currency: 'USD' | 'NGN' | string;
    min_salary_range_amount: string;
    max_salary_range_amount: string;
    require_employment_type: boolean;
    require_work_mode?: boolean;
    require_experience_level: boolean;
    require_years_of_experience: boolean;
    require_work_environment: boolean;
    require_salary_range: boolean;
    display_salary?: boolean;
    require_cv_upload: boolean;
    require_portfolio_upload?: boolean;
    assessment: number;
    hide_salary_in_form?: boolean;
    require_salary_in_form?: boolean;
    show_salary_in_form?: boolean;
  } | null;
}

/**
 * Interface for assessment info response
 */
export interface AssessmentResponse {
  message: string;
  data: AssessmentData;
}
/**
 * Hook for fetching assessment info using TanStack React Query
 */
export const useGetAssessmentInfo = (assessmentId: string | null) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['assessmentInfo', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<AssessmentResponse>(
        '/candidate/assessment/',
        { params: { assessment: assessmentId } }
      );
      return response.data;
    },
    staleTime: Infinity, // never becomes stale
    enabled: !!assessmentId, // only run if we have an ID
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    assessmentInfo: data,
    isLoading,
    error,
    fetchAssessmentInfo: () =>
      queryClient.invalidateQueries({ queryKey: ['assessmentInfo', assessmentId] }),
  };
};
